// === SAA:UI:SELECTION-CARD ===
// Light-tinted "Uw selectie" sidebar card (steps 2+): the products picked on step 1,
// with three states per row read from EXISTING state only:
//   done        — a calculated premium is present in the stored engine output
//                 (filled check disc)
//   current     — the product screen you are on (route match); replaces the
//                 old ProductIndicator chip row's job (bold, brighter row)
//   selected    — selected on step 1, neither of the above (plain check)
// Purely a view — no state of its own, and rows are NOT clickable (navigation
// from here is a separately queued decision).

import React from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWorkflow } from '@sluitstraat/core';
import { Check } from 'lucide-react';
import { commonStyles } from '../styles/commonStyles';
import { productIcons, ProductIconId } from '../icons/productRegistry';
import { PRODUCT_CONTENT } from '../products/content';
import { getProductOrder } from '../workflowConfig';
import { hasCalculatedPremium } from '../utils/productPremium';

export const SelectionCard: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { selectedProducts, getStepData } = useWorkflow();

  const selected = (getProductOrder() as string[]).filter((id) => selectedProducts?.[id]);
  if (selected.length === 0) return null;

  const isDone = (id: string): boolean => hasCalculatedPremium(id, getStepData(id));

  return (
    <div style={commonStyles.selectionCard}>
      <p style={commonStyles.selectionCardEyebrow}>
        {t('products.productsSelected', { count: selected.length })}
      </p>
      <h3 style={commonStyles.selectionCardTitle}>{t('sidebar.selection.title')}</h3>
      <ul style={commonStyles.selectionCardList}>
        {selected.map((id) => {
          const Icon = productIcons[id as ProductIconId];
          const content = PRODUCT_CONTENT[id as keyof typeof PRODUCT_CONTENT];
          const current = pathname === `/workflow/${id}`;
          const done = isDone(id);
          return (
            <li
              key={id}
              style={{
                ...commonStyles.selectionCardItem,
                ...(current ? commonStyles.selectionCardItemCurrent : {}),
              }}
            >
              <span style={commonStyles.selectionCardItemIcon}>
                {Icon && <Icon size={18} weight="regular" aria-hidden="true" />}
              </span>
              <span>{content ? t(content.displayNameKey) : id}</span>
              {done ? (
                <span style={commonStyles.selectionCardCheckDone}>
                  <Check size={12} strokeWidth={3} aria-hidden="true" />
                </span>
              ) : (
                <Check size={16} strokeWidth={2} style={commonStyles.selectionCardCheck} aria-hidden="true" />
              )}
            </li>
          );
        })}
      </ul>
      <div style={commonStyles.selectionCardFooter}>{t('sidebar.selection.tagline')}</div>
    </div>
  );
};

export default SelectionCard;
